/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { authApi } from './lib/api';
import ErrorBoundary, { reloadOnceForChunkFailure } from './components/shared/ErrorBoundary';
import { ToastProvider } from './components/shared/Toast';
import { DarkModeContext, getStoredTheme, storeTheme, applyTheme, type Theme } from './lib/darkMode';

/**
 * Retry a dynamic import ONCE per session, by reloading the page.
 *
 * A deploy replaces the hashed chunk filenames, so a tab holding the old
 * index.html asks for a chunk that no longer exists; one reload picks up the
 * new index.html and fixes it. That is the only failure a reload can fix.
 *
 * Everything else — a dropped fetch on a phone, or an error thrown while the
 * module evaluates, which rejects this same promise — comes back identically
 * after the reload. Reloading again on that was an unbounded loop: the route
 * chunk fails, the page reloads, the same chunk fails, forever, showing
 * nothing but the Suspense spinner. It bit the ~1 MB AdminPortal chunk on iOS
 * Safari while the much smaller UserPortal chunk loaded fine.
 *
 * So the reload happens at most once (the budget is shared with ErrorBoundary),
 * and after that the rejection is rethrown and reaches ErrorBoundary, which at
 * least names the failure instead of spinning.
 */
function lazyRetry<T extends React.ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  return lazy(() =>
    loader().catch((err) => {
      if (reloadOnceForChunkFailure()) {
        // Reload is underway; never resolve, so nothing renders in the meantime.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    })
  );
}

const LandingPage = lazyRetry(() => import('./views/LandingPage'));
const AuthPage = lazyRetry(() => import('./views/AuthPage'));
const AdminPortal = lazyRetry(() => import('./views/AdminPortal'));
const UserPortal = lazyRetry(() => import('./views/UserPortal'));
const ResetPasswordPage = lazyRetry(() => import('./views/ResetPasswordPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      // Global mutation error — toasts are fired by individual mutation callers
      console.error('Mutation error:', error.message);
    },
  }),
});

export type UserRole = 'guest' | 'user' | 'admin';

export interface AuthUser {
  id: string;
  firstName: string;
  username: string;
  role: UserRole;
  roles: string[];
  email: string;
  requires2FASetup?: boolean;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-200 dark:border-slate-700 border-t-fy-primary" />
    </div>
  );
}

export default function App() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [isDark, setIsDark] = useState(() => applyTheme(getStoredTheme()));

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    storeTheme(t);
    setIsDark(applyTheme(t));
  }, []);

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (theme === 'system') setIsDark(applyTheme('system'));
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const [user, setUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Clean up legacy localStorage tokens (migrated to httpOnly cookies)
  useEffect(() => {
    localStorage.removeItem('fy_auth_token');
    localStorage.removeItem('fy_refresh_token');
  }, []);

  // On mount, check auth state via raw fetch (bypasses request() 401->redirect loop)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/me', { credentials: 'include' });
        if (res.ok) {
          const { data } = await res.json() as { data: { id: string; firstName: string; username: string; roles: string[]; email?: string; portalMode?: 'user' | 'admin'; totpEnabled?: boolean; permissions?: string[] } };
          if (!cancelled) {
            const roles = data.roles || ['user'];
            const portalMode = data.portalMode ?? 'user';
            const perms: string[] = data.permissions ?? [];
            const twoFaKey = portalMode === 'admin' ? 'admin_2fa_required' : 'user_2fa_required';
            const needs2FA = perms.includes(twoFaKey) && !data.totpEnabled;
            setUser({
              id: data.id,
              firstName: data.firstName,
              username: data.username,
              roles,
              role: portalMode === 'admin' ? 'admin' : 'user',
              email: data.email ?? '',
              requires2FASetup: needs2FA,
            });
          }
        }
        // If not ok (401/etc), user stays null — not logged in
      } catch {
        // Network error — stay logged out
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = (authUser: AuthUser) => {
    setUser(authUser);
  };

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
    queryClient.clear();
  };

  // Show loading screen until auth check completes
  if (!authChecked) {
    return (
      <DarkModeContext.Provider value={{ theme, setTheme, isDark }}>
        <LoadingScreen />
      </DarkModeContext.Provider>
    );
  }

  return (
    <DarkModeContext.Provider value={{ theme, setTheme, isDark }}>
    <QueryClientProvider client={queryClient}>
    <ToastProvider>
    <ErrorBoundary>
    <Router>
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/auth"
          element={
            user
              ? <Navigate to="/portal" replace />
              : <AuthPage onLogin={handleLogin} />
          }
        />

        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

        {/* Protected Routes */}
        <Route
          path="/portal/*"
          element={
            user ? (
              user.role === 'admin'
                ? <AdminPortal user={{ id: user.id, firstName: user.firstName, role: user.role, roles: user.roles, requires2FASetup: user.requires2FASetup }} onLogout={handleLogout} />
                : <UserPortal user={{ id: user.id, firstName: user.firstName, role: user.role, roles: user.roles, requires2FASetup: user.requires2FASetup }} onLogout={handleLogout} />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Router>
    </ErrorBoundary>
    </ToastProvider>
    </QueryClientProvider>
    </DarkModeContext.Provider>
  );
}
